import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import {
  getEmailConfig, saveEmailConfig, testEmailConnection,
  checkInboxNow, getParsedEmails, applyEmailUpdate, dismissEmail
} from '../lib/api';
import { useI18n } from '../contexts/I18nContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  Mail, Settings, RefreshCw, Check, X, AlertTriangle,
  Package, Clock, ChevronDown, ChevronUp, Zap, Eye, EyeOff
} from 'lucide-react';

function ConfigSection({ config, onSave }) {
  const { t } = useI18n();
  const [form, setForm] = useState({
    imap_host: '',
    imap_port: 993,
    email_address: '',
    password: '',
    use_ssl: true,
    check_folder: 'INBOX',
    auto_check_enabled: false,
    check_interval_minutes: 30,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (config?.configured) {
      setForm(prev => ({
        ...prev,
        imap_host: config.imap_host || '',
        imap_port: config.imap_port || 993,
        email_address: config.email_address || '',
        use_ssl: config.use_ssl !== undefined ? config.use_ssl : true,
        check_folder: config.check_folder || 'INBOX',
        auto_check_enabled: config.auto_check_enabled || false,
        check_interval_minutes: config.check_interval_minutes || 30,
      }));
    }
  }, [config]);

  const handleSave = async () => {
    if (!form.imap_host || !form.email_address) {
      toast.error(t('emailSettings.hostRequired'));
      return;
    }
    if (!form.password && !config?.password_set) {
      toast.error(t('emailSettings.passwordRequired'));
      return;
    }

    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.password && config?.password_set) {
        payload.password = '___UNCHANGED___';
      }
      await saveEmailConfig(payload);
      toast.success(t('emailSettings.configSaved'));
      if (onSave) onSave();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('emailSettings.errorSaving'));
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await testEmailConnection();
      if (res.data.success) {
        toast.success(res.data.message);
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t('emailSettings.errorTestingConn'));
    } finally {
      setTesting(false);
    }
  };

  const gmailPreset = () => {
    setForm(prev => ({ ...prev, imap_host: 'imap.gmail.com', imap_port: 993, use_ssl: true }));
  };

  const outlookPreset = () => {
    setForm(prev => ({ ...prev, imap_host: 'outlook.office365.com', imap_port: 993, use_ssl: true }));
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 space-y-5">
      <div className="flex items-center gap-2 mb-2">
        <Settings size={20} className="text-primary" />
        <h2 className="font-heading text-lg">{t('emailSettings.imapConfig')}</h2>
      </div>

      <div className="flex gap-2 mb-4">
        <Button size="sm" variant="outline" onClick={gmailPreset} data-testid="gmail-preset">Gmail</Button>
        <Button size="sm" variant="outline" onClick={outlookPreset} data-testid="outlook-preset">Outlook</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>{t('emailSettings.imapHost')}</Label>
          <Input
            value={form.imap_host}
            onChange={(e) => setForm({...form, imap_host: e.target.value})}
            placeholder="imap.gmail.com"
            data-testid="imap-host-input"
          />
        </div>
        <div className="space-y-2">
          <Label>{t('emailSettings.port')}</Label>
          <Input
            type="number"
            value={form.imap_port}
            onChange={(e) => setForm({...form, imap_port: parseInt(e.target.value) || 993})}
            data-testid="imap-port-input"
          />
        </div>
        <div className="space-y-2">
          <Label>{t('emailSettings.emailAddress')}</Label>
          <Input
            type="email"
            value={form.email_address}
            onChange={(e) => setForm({...form, email_address: e.target.value})}
            placeholder="tu@correo.com"
            data-testid="email-address-input"
          />
        </div>
        <div className="space-y-2">
          <Label>{t('emailSettings.passwordLabel')} {config?.password_set && <span className="text-xs text-muted-foreground">({t('emailSettings.passwordSet')})</span>}</Label>
          <div className="relative">
            <Input
              type={showPassword ? 'text' : 'password'}
              value={form.password}
              onChange={(e) => setForm({...form, password: e.target.value})}
              placeholder={config?.password_set ? t('emailSettings.leaveEmpty') : 'App Password'}
              data-testid="email-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <p className="text-xs text-muted-foreground">
            {t('emailSettings.appPasswordHint')}
          </p>
        </div>
        <div className="space-y-2">
          <Label>{t('emailSettings.folder')}</Label>
          <Input
            value={form.check_folder}
            onChange={(e) => setForm({...form, check_folder: e.target.value})}
            placeholder="INBOX"
            data-testid="folder-input"
          />
        </div>
        <div className="space-y-2">
          <Label>{t('emailSettings.interval')}</Label>
          <Input
            type="number"
            value={form.check_interval_minutes}
            onChange={(e) => setForm({...form, check_interval_minutes: parseInt(e.target.value) || 30})}
            min={5}
            data-testid="interval-input"
          />
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Switch
          checked={form.use_ssl}
          onCheckedChange={(v) => setForm({...form, use_ssl: v})}
        />
        <Label>{t('emailSettings.useSSL')}</Label>
      </div>

      <div className="flex gap-3 pt-2">
        <Button onClick={handleSave} disabled={saving} data-testid="save-config-btn">
          {saving ? <RefreshCw size={16} className="mr-2 animate-spin" /> : <Check size={16} className="mr-2" />}
          {t('common.save')}
        </Button>
        <Button variant="outline" onClick={handleTest} disabled={testing || !config?.configured} data-testid="test-connection-btn">
          {testing ? <RefreshCw size={16} className="mr-2 animate-spin" /> : <Zap size={16} className="mr-2" />}
          {t('emailSettings.testConnection')}
        </Button>
      </div>

      {config?.last_check && (
        <div className="text-sm text-muted-foreground flex items-center gap-2 pt-2">
          <Clock size={14} />
          {t('emailSettings.lastCheck')}: {new Date(config.last_check).toLocaleString('es-MX')}
          {config.last_check_status === 'success' ? (
            <span className="text-emerald-400 flex items-center gap-1"><Check size={14} /> ({config.last_check_count} emails)</span>
          ) : config.last_check_status ? (
            <span className="text-red-400 flex items-center gap-1"><X size={14} /> {config.last_check_status}</span>
          ) : null}
        </div>
      )}
    </div>
  );
}

function ParsedEmailsList() {
  const { t } = useI18n();
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [filter, setFilter] = useState('pending');
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => { loadEmails(); }, [filter]);

  const loadEmails = async () => {
    try {
      setLoading(true);
      const params = filter !== 'all' ? { status: filter } : {};
      const res = await getParsedEmails(params);
      setEmails(res.data.emails || []);
    } catch (err) {
      toast.error(t('emailSettings.errorLoadingEmails'));
    } finally {
      setLoading(false);
    }
  };

  const handleCheckNow = async () => {
    setChecking(true);
    try {
      const res = await checkInboxNow();
      if (res.data.success) {
        toast.success(res.data.message);
        loadEmails();
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t('emailSettings.errorCheckingInbox'));
    } finally {
      setChecking(false);
    }
  };

  const handleApply = async (emailId) => {
    try {
      const res = await applyEmailUpdate(emailId);
      toast.success(res.data.message);
      loadEmails();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al aplicar');
    }
  };

  const handleDismiss = async (emailId) => {
    try {
      await dismissEmail(emailId);
      toast.success(t('emailSettings.emailDismissed'));
      loadEmails();
    } catch (err) {
      toast.error(t('emailSettings.errorDismissing'));
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Mail size={20} className="text-primary" />
          <h2 className="font-heading text-lg">{t('emailSettings.parsedEmails')}</h2>
        </div>
        <Button
          size="sm"
          onClick={handleCheckNow}
          disabled={checking}
          data-testid="check-inbox-btn"
        >
          {checking ? <RefreshCw size={14} className="mr-2 animate-spin" /> : <RefreshCw size={14} className="mr-2" />}
          {t('emailSettings.checkNow')}
        </Button>
      </div>

      <div className="flex gap-2">
        {['pending', 'applied', 'dismissed', 'not_relevant', 'all'].map(s => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
              filter === s ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground hover:text-foreground'
            }`}
            data-testid={`filter-${s}`}
          >
            {s === 'pending' ? t('emailSettings.pendingEmails') :
             s === 'applied' ? t('emailSettings.applied') :
             s === 'dismissed' ? t('emailSettings.dismissed') :
             s === 'not_relevant' ? t('emailSettings.notRelevant') : t('common.all')}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-8 text-muted-foreground animate-pulse">{t('common.loading')}</div>
      ) : emails.length === 0 ? (
        <div className="text-center py-8">
          <Mail size={32} className="mx-auto mb-2 text-muted-foreground/40" />
          <p className="text-muted-foreground text-sm">{t('emailSettings.noEmails')}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {emails.map(em => (
            <div key={em.id} className="border border-border rounded-lg overflow-hidden">
              <div
                className="p-3 flex items-center justify-between cursor-pointer hover:bg-secondary/20"
                onClick={() => setExpandedId(expandedId === em.id ? null : em.id)}
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  {em.is_shipping_related ? (
                    <Package size={16} className="text-primary flex-shrink-0" />
                  ) : (
                    <Mail size={16} className="text-muted-foreground flex-shrink-0" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{em.subject || t('emailSettings.noSubject')}</p>
                    <p className="text-xs text-muted-foreground truncate">{em.from}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    em.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                    em.status === 'applied' ? 'bg-emerald-500/20 text-emerald-400' :
                    em.status === 'dismissed' ? 'bg-muted text-muted-foreground' :
                    'bg-secondary text-muted-foreground'
                  }`}>
                    {em.status === 'pending' ? t('emailSettings.pendingEmails') :
                     em.status === 'applied' ? t('emailSettings.applied') :
                     em.status === 'dismissed' ? t('emailSettings.dismissed') : t('emailSettings.notRelevant')}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {new Date(em.received_at).toLocaleDateString('es-MX')}
                  </span>
                  {expandedId === em.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </div>
              </div>

              {expandedId === em.id && (
                <div className="border-t border-border p-4 space-y-3 bg-secondary/10">
                  <p className="text-sm text-muted-foreground">{em.body_preview}</p>

                  {em.is_shipping_related && em.extracted_data && (
                    <div className="bg-secondary/30 rounded-lg p-3 space-y-2">
                      <h4 className="text-sm font-medium flex items-center gap-1">
                        <AlertTriangle size={14} className="text-amber-400" />
                        {t('emailSettings.extractedData')}
                      </h4>
                      {em.extracted_data.container_numbers?.length > 0 && (
                        <div className="text-sm">
                          <span className="text-muted-foreground">{t('emailSettings.containersFound')}: </span>
                          <span className="font-mono">{em.extracted_data.container_numbers.join(', ')}</span>
                        </div>
                      )}
                      {em.extracted_data.mbl_numbers?.length > 0 && (
                        <div className="text-sm">
                          <span className="text-muted-foreground">{t('emailSettings.mblsFound')}: </span>
                          <span className="font-mono">{em.extracted_data.mbl_numbers.join(', ')}</span>
                        </div>
                      )}
                      {em.extracted_data.status_update && (
                        <div className="text-sm">
                          <span className="text-muted-foreground">{t('emailSettings.detectedStatus')}: </span>
                          <span className="text-primary">{em.extracted_data.status_update}</span>
                        </div>
                      )}
                      {em.extracted_data.eta && (
                        <div className="text-sm">
                          <span className="text-muted-foreground">ETA: </span>
                          <span>{em.extracted_data.eta}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {em.status === 'pending' && (
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleApply(em.id)} data-testid={`apply-email-${em.id}`}>
                        <Check size={14} className="mr-1" /> {t('emailSettings.applyData')}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleDismiss(em.id)} data-testid={`dismiss-email-${em.id}`}>
                        <X size={14} className="mr-1" /> {t('emailSettings.dismiss')}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function EmailSettingsPage() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t } = useI18n();

  useEffect(() => { loadConfig(); }, []);

  const loadConfig = async () => {
    try {
      const res = await getEmailConfig();
      setConfig(res.data);
    } catch (err) {
      toast.error(t('emailSettings.errorLoadingConfig'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground tracking-tight" data-testid="email-settings-title">
            {t('emailSettings.title')}
          </h1>
          <p className="text-muted-foreground mt-1">{t('emailSettings.subtitle')}</p>
        </div>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground animate-pulse">{t('common.loading')}</div>
        ) : (
          <>
            <ConfigSection config={config} onSave={loadConfig} />
            {config?.configured && <ParsedEmailsList />}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
